import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Undo2 } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const ReviewItem = ({ item, onDone }: { item: any; onDone: () => void }) => {
  const [mode, setMode] = useState<null | "reject" | "needs_changes">(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isAuthor = item.reason === "author_submission" || item.reason === "author_resubmission";

  const act = async (action: string, withNote?: string) => {
    setBusy(true);
    try {
      await api.patch(`/admin/review-queue/${item.id}`, { action, note: withNote || undefined });
      toast.success(action === "approve" ? "Approved & published"
        : action === "needs_changes" ? "Sent back to the author with your note" : "Rejected");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const confirmNote = () => {
    if (note.trim().length < 5) { toast.error("Write a note the author will see (5+ chars)"); return; }
    act(mode!, note.trim());
  };

  return (
    <div data-testid={`review-item-${item.id}`}
      className="bg-[#121212] border border-white/10 rounded-lg p-5">
      <div className="flex flex-col sm:flex-row gap-4">
        {item.article?.hero_image_url && (
          <img src={item.article.hero_image_url} alt="" className="w-full sm:w-36 h-24 object-cover rounded shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <StatusBadge status={item.reason} />
            <span className="font-mono text-[10px] text-gray-500">
              confidence {item.confidence_score != null ? `${Math.round(item.confidence_score * 100)}%` : "n/a"} · {timeAgo(item.created_at)}
            </span>
          </div>
          <h3 className="text-white font-semibold leading-snug">{item.article?.title || item.title}</h3>
          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{item.article?.summary}</p>
          <p className="font-mono text-[10px] text-gray-500 mt-2 uppercase">
            {item.article?.source_name} · {(item.article?.categories || []).join(", ")}
          </p>
        </div>
        <div className="flex sm:flex-col gap-2 shrink-0">
          <Button size="sm" disabled={busy} onClick={() => act("approve")} data-testid={`review-approve-${item.id}`}
            className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 flex-1">
            <Check className="h-4 w-4 mr-1" /> Approve
          </Button>
          {isAuthor && (
            <Button size="sm" disabled={busy} data-testid={`review-changes-${item.id}`}
              onClick={() => setMode(mode === "needs_changes" ? null : "needs_changes")}
              className="bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 flex-1">
              <Undo2 className="h-4 w-4 mr-1" /> Changes…
            </Button>
          )}
          <Button size="sm" disabled={busy} data-testid={`review-reject-${item.id}`}
            onClick={() => (isAuthor ? setMode(mode === "reject" ? null : "reject") : act("reject"))}
            className="bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 flex-1">
            <X className="h-4 w-4 mr-1" /> {isAuthor ? "Reject…" : "Reject"}
          </Button>
        </div>
      </div>
      {mode && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder={mode === "needs_changes"
              ? "What should the author change? They receive this note by email and in the Studio."
              : "Why is this rejected? The author receives this note."}
            data-testid={`review-note-${item.id}`}
            className="bg-[#1A1A1A] border-white/10 text-white flex-1" />
          <Button size="sm" disabled={busy} onClick={confirmNote} data-testid={`review-note-confirm-${item.id}`}
            className={`shrink-0 self-start ${mode === "needs_changes"
              ? "bg-amber-500 text-black hover:bg-amber-400"
              : "bg-red-500 text-white hover:bg-red-600"}`}>
            {mode === "needs_changes" ? "Send back to author" : "Confirm rejection"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default function ReviewQueue() {
  const queryClient = useQueryClient();
  const { data: items } = useQuery({
    queryKey: ["admin-review"],
    queryFn: async () => (await api.get("/admin/review-queue", { params: { status: "open" } })).data,
    refetchInterval: 20000,
  });

  const onDone = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-review"] });
    queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
  };

  return (
    <div data-testid="admin-review-page">
      <PageHeader title="Review Queue" sub="Author submissions and low-confidence imports awaiting a human decision" />
      {(items || []).length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg py-16 text-center">
          <p className="text-gray-500">Queue is clear — automation is handling everything.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(items || []).map((item: any) => <ReviewItem key={item.id} item={item} onDone={onDone} />)}
        </div>
      )}
    </div>
  );
}
